import type { components } from "@/shared/lib/api.types";

type RaidContentOption = components["schemas"]["RaidContentOption"];

// Pure selection-decision logic for the raid-content chooser: given what was selected before this
// sync and the currently available options, decide which raid instances should stay selected.
// - Legacy selections (selected but no longer eligible for the date) are retained but pruned to
//   whatever the server still reports as selected.
// - Otherwise selections are constrained to eligible options: none → [], exactly one → auto-select
//   it, many → keep the prior selection intersected with the eligible set.
export function reconcileSelectedIds(input: {
  firstSynchronization: boolean;
  priorConfiguredIds: string[];
  currentIds: string[];
  hasLegacySelections: boolean;
  options: RaidContentOption[];
  eligibleOptions: RaidContentOption[];
}): string[] {
  const selectedIds = input.firstSynchronization
    ? input.priorConfiguredIds
    : input.currentIds;

  if (input.hasLegacySelections) {
    const retainedIds = new Set(
      input.options
        .filter((option) => option.isSelected)
        .map((option) => option.id),
    );
    return selectedIds.filter((id) => retainedIds.has(id));
  }
  if (input.eligibleOptions.length === 0) return [];
  if (input.eligibleOptions.length === 1) return [input.eligibleOptions[0].id];
  const eligibleIds = new Set(input.eligibleOptions.map((option) => option.id));
  return selectedIds.filter((id) => eligibleIds.has(id));
}
